<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { isPhoneE164OrEmpty } from 'shared/helpers/Validators';
import NextButton from 'dashboard/components-next/button/Button.vue';
import EvolutionConnection from '../components/EvolutionConnection.vue';

const store = useStore();
const router = useRouter();
const { t } = useI18n();

const inboxName = ref('');
const phoneNumber = ref('');
const createdInboxId = ref(null);

const rules = {
  inboxName: { required },
  phoneNumber: { required, isPhoneE164OrEmpty },
};
const v$ = useVuelidate(rules, { inboxName, phoneNumber });
const uiFlags = computed(() => store.getters['inboxes/getUIFlags']);

const createChannel = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  try {
    const inbox = await store.dispatch('inboxes/createChannel', {
      name: inboxName.value.trim(),
      channel: {
        type: 'whatsapp',
        phone_number: phoneNumber.value,
        provider: 'evolution',
        provider_config: {},
      },
    });
    createdInboxId.value = inbox.id;
  } catch (error) {
    useAlert(error.message || t('INBOX_MGMT.ADD.WHATSAPP.API.ERROR_MESSAGE'));
  }
};

const goToAgents = () => {
  router.replace({
    name: 'settings_inboxes_add_agents',
    params: { page: 'new', inbox_id: createdInboxId.value },
  });
};
</script>

<template>
  <div v-if="createdInboxId" class="flex flex-col gap-4">
    <EvolutionConnection :inbox-id="createdInboxId" connect-on-mount />
    <div>
      <NextButton
        :label="$t('INBOX_MGMT.WHATSAPP_EVOLUTION.CONTINUE')"
        @click="goToAgents"
      />
    </div>
  </div>

  <form
    v-else
    class="flex flex-wrap flex-col mx-0"
    @submit.prevent="createChannel"
  >
    <div class="flex-shrink-0 flex-grow-0">
      <label :class="{ error: v$.inboxName.$error }">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.LABEL') }}
        <input
          v-model="inboxName"
          type="text"
          :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.PLACEHOLDER')"
          @blur="v$.inboxName.$touch"
        />
        <span v-if="v$.inboxName.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.ERROR') }}
        </span>
      </label>
    </div>

    <div class="flex-shrink-0 flex-grow-0">
      <label :class="{ error: v$.phoneNumber.$error }">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.LABEL') }}
        <input
          v-model="phoneNumber"
          type="text"
          :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.PLACEHOLDER')"
          @blur="v$.phoneNumber.$touch"
        />
        <span v-if="v$.phoneNumber.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.ERROR') }}
        </span>
      </label>
      <p class="mb-4 text-xs text-n-slate-11">
        {{ $t('INBOX_MGMT.WHATSAPP_EVOLUTION.PHONE_HELP') }}
      </p>
    </div>

    <div class="w-full">
      <NextButton
        type="submit"
        :label="$t('INBOX_MGMT.WHATSAPP_EVOLUTION.CREATE')"
        :is-loading="uiFlags.isCreating"
      />
    </div>
  </form>
</template>
